import { useEffect, useState, useCallback } from "react";
import {
  RefreshCw, CheckCircle2, XCircle, Lock, LogOut, Loader2,
  Download, Filter, X, Eye, EyeOff, FileText, ChevronDown, ChevronUp, Save,
  PlusCircle, BookOpen, Trash2, FileDown, Shield, Monitor, Edit2,
  Truck, AlertTriangle, MessageSquare, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { api, getToken, setToken, clearToken } from "@/lib/api";
import { Overline } from "@/components/Section";
import SEOHead from "@/components/SEOHead";

const ADMIN_NAME = "Andrej Sasaroga";
const CHANNEL_OPTIONS = ["Phone Call", "Email", "Tenstreet", "IntelliApp", "Driver Pulse", "In-Person", "Other"];
const ACTIVE_DAYS = 45;

const STATUS_OPTIONS = [
  { key: "new",              label: "In System",     color: "bg-slate-100 text-slate-700 border-slate-200" },
  { key: "documents_needed", label: "Docs Needed",   color: "bg-amber-50 text-amber-700 border-amber-200" },
  { key: "approved",         label: "Approved",      color: "bg-green-50 text-green-700 border-green-200" },
  { key: "not_approved",     label: "Not Approved",  color: "bg-red-50 text-red-700 border-red-200" },
  { key: "schedule_call",    label: "Schedule Call", color: "bg-blue-50 text-blue-700 border-blue-200" },
];

function StatusBadge({ status }) {
  const opt = STATUS_OPTIONS.find((o) => o.key === status) || STATUS_OPTIONS[0];
  return (
    <span className={`font-head text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border ${opt.color}`}>
      {opt.label}
    </span>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtFull(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
  });
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function daysLeftFn(intro) {
  const ms = Date.now() - new Date(intro.created_at).getTime();
  return Math.max(0, Math.ceil(ACTIVE_DAYS - ms / (1000 * 60 * 60 * 24)));
}
function isActiveFn(intro) { return daysLeftFn(intro) > 0; }

/* ─── Document viewer modal ─── */
function DocModal({ path, label, onClose, appId, onSignatureCleared }) {
  const [src, setSrc]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImage, setIsImage] = useState(true);
  const [errMsg, setErrMsg]  = useState(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    if (!path) { setLoading(false); setErrMsg("No file path recorded."); return; }
    setLoading(true); setErrMsg(null);
    api.getFile(path)
      .then((blob) => {
        setIsImage(blob.type !== "application/pdf");
        setSrc(URL.createObjectURL(blob));
        setLoading(false);
      })
      .catch((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 404) {
          setErrMsg("File not found in storage. The signature was recorded but the original file is no longer available (this can happen with applications submitted before the current storage system). You can clear the invalid path below.");
        } else if (status === 401 || status === 403) {
          setErrMsg("Session expired — please log out and log in again to view this document.");
        } else {
          setErrMsg(detail || "Could not load document. Please try again.");
        }
        setLoading(false);
      });
  }, [path]);

  async function handleClearPath() {
    if (!appId) return;
    setClearing(true);
    try {
      await api.clearSignaturePath(appId);
      toast.success("Invalid signature path cleared.");
      if (onSignatureCleared) onSignatureCleared(appId);
      onClose();
    } catch { toast.error("Failed to clear path."); }
    finally { setClearing(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="doc-modal">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] flex flex-col border border-border">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <p className="font-head text-sm uppercase tracking-[0.2em]">{label}</p>
          <button onClick={onClose} className="text-muted-foreground hover:text-[#0A0A0A]" data-testid="close-doc-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-auto p-4">
          {loading && <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}
          {!loading && src && isImage && <img src={src} alt={label} className="max-w-full mx-auto" />}
          {!loading && src && !isImage && <iframe src={src} title={label} className="w-full h-96 border-0" />}
          {!loading && errMsg && (
            <div className="flex flex-col items-center gap-4 py-8 px-4 text-center">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <X className="w-5 h-5 text-red-500" />
              </div>
              <p className="text-sm text-muted-foreground max-w-md">{errMsg}</p>
              {errMsg.includes("no longer available") && appId && (
                <button onClick={handleClearPath} disabled={clearing}
                  data-testid="clear-signature-path-btn"
                  className="mt-2 px-4 py-2 text-xs font-head uppercase tracking-[0.15em] border border-red-200 text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50">
                  {clearing ? "Clearing..." : "Clear Invalid Path"}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Shared modal shell ─── */
function IntroFormModal({ title, subtitle, initial, apps, onClose, onSubmit, submitLabel = "Save" }) {
  const fldCls = "w-full bg-transparent border-b-2 border-border py-2.5 text-[#0A0A0A] placeholder:text-muted-foreground focus:outline-none focus:border-[#0A0A0A] transition-colors text-sm";
  const lblCls = "block font-head text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1";

  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleDriverSelect = (e) => {
    const appId = e.target.value;
    const app = apps.find((a) => a.id === appId);
    if (app) setForm((f) => ({ ...f, driver_id: appId, driver_name: `${app.first_name} ${app.last_name}`.trim() }));
    else setForm((f) => ({ ...f, driver_id: "", driver_name: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.driver_name.trim() || !form.carrier_name.trim()) {
      toast.error("Driver name and carrier name are required."); return;
    }
    if (form.channel === "Other" && !form.channel_other.trim()) {
      toast.error("Please specify the channel details."); return;
    }
    setSubmitting(true);
    try {
      await onSubmit(form);
      onClose();
    } catch {
      toast.error("Operation failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="intro-modal">
      <div className="bg-white w-full max-w-lg border border-border max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white z-10">
          <div>
            {subtitle && <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{subtitle}</p>}
            <p className="font-head text-sm uppercase tracking-[0.2em] mt-0.5">{title}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-[#0A0A0A]" data-testid="close-intro-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className={lblCls}>Select From Applications (optional)</label>
            <select
              data-testid="intro-driver-select"
              value={form.driver_id || ""}
              onChange={handleDriverSelect}
              className="w-full border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]"
            >
              <option value="">— Type driver name below instead —</option>
              {apps.map((a) => (
                <option key={a.id} value={a.id}>{a.first_name} {a.last_name} · {a.phone}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={lblCls}>Driver Name *</label>
            <input data-testid="intro-driver-name" value={form.driver_name} onChange={set("driver_name")} className={fldCls} placeholder="Full name" />
          </div>
          <div>
            <label className={lblCls}>Carrier / Company Name *</label>
            <input data-testid="intro-carrier-name" value={form.carrier_name} onChange={set("carrier_name")} className={fldCls} placeholder="e.g. JB Hunt, Hub Group" />
          </div>
          <div>
            <label className={lblCls}>Introduction Channel *</label>
            <select
              data-testid="intro-channel"
              value={form.channel}
              onChange={set("channel")}
              className="w-full border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]"
            >
              {CHANNEL_OPTIONS.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          {form.channel === "Other" && (
            <div>
              <label className={lblCls}>Specify Channel *</label>
              <input data-testid="intro-channel-other" value={form.channel_other} onChange={set("channel_other")} className={fldCls} placeholder="Describe the channel used" />
            </div>
          )}
          <div>
            <label className={lblCls}>Notes (optional)</label>
            <textarea data-testid="intro-notes" value={form.notes} onChange={set("notes")} rows={3} className={`${fldCls} resize-none`} placeholder="Any relevant details…" />
          </div>
          <button
            type="submit"
            data-testid="intro-submit-btn"
            disabled={submitting}
            className="w-full font-head text-sm uppercase tracking-[0.2em] bg-primary text-black py-4 hover:bg-[#0A0A0A] hover:text-primary transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : submitLabel}
          </button>
        </form>
      </div>
    </div>
  );
}

const NEW_INTRO_DEFAULT = { driver_id: "", driver_name: "", carrier_name: "", channel: "Phone Call", channel_other: "", notes: "" };

/* ─── Application row with full detail drawer ─── */
function AppRow({ a, driverIntros, onViewDoc, onStatusChange, onUpdateIntro, onDownloadIntroPDF, onDownloadDriverPDF }) {
  const [open, setOpen] = useState(false);
  const [newStatus, setNewStatus] = useState(a.status || "new");
  const [saving, setSaving] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(a.status || "new");
  const [fleetValue, setFleetValue] = useState(a.current_fleet || "");
  const [savingFleet, setSavingFleet] = useState(false);
  const [editingIntro, setEditingIntro] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [ssnVisible, setSsnVisible] = useState(false);
  const [ssnValue, setSsnValue] = useState(null);   // null = not fetched, "" = no SSN
  const [loadingSSN, setLoadingSSN] = useState(false);

  const handleRevealSSN = async (e) => {
    e.stopPropagation();
    if (ssnVisible) { setSsnVisible(false); return; }
    if (ssnValue !== null) { setSsnVisible(true); return; }
    setLoadingSSN(true);
    try {
      const data = await api.revealSSN(a.id);
      setSsnValue(data.ssn || "Not provided");
      setSsnVisible(true);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error("Session expired — please log out and log in again.");
      } else if (status === 404) {
        toast.error("Application not found.");
      } else {
        toast.error("Could not retrieve SSN. Try refreshing the page.");
      }
    } finally { setLoadingSSN(false); }
  };

  const saveStatus = async (e) => {
    e.stopPropagation();
    if (newStatus === currentStatus) return;
    setSaving(true);
    try {
      await api.updateApplicationStatus(a.id, newStatus);
      setCurrentStatus(newStatus);
      onStatusChange(a.id, newStatus);
      toast.success("Status updated.");
    } catch {
      toast.error("Failed to update status.");
      setNewStatus(currentStatus);
    } finally { setSaving(false); }
  };

  const saveFleet = async (e) => {
    e.stopPropagation();
    setSavingFleet(true);
    try {
      await api.updateApplicationFleet(a.id, fleetValue);
      toast.success("Fleet updated.");
    } catch {
      toast.error("Failed to update fleet.");
    } finally { setSavingFleet(false); }
  };

  const downloadPDF = async (e) => {
    e.stopPropagation();
    setDownloadingPDF(true);
    try {
      const blob = await onDownloadDriverPDF(a.id);
      triggerDownload(blob, `hylas-driver-${a.first_name}-${a.last_name}.pdf`.toLowerCase().replace(/ /g, "-"));
    } catch {
      toast.error("Could not generate PDF.");
    } finally { setDownloadingPDF(false); }
  };

  return (
    <>
      {editingIntro && (
        <IntroFormModal
          title="Edit Introduction"
          subtitle={`ID: ${editingIntro.intro_id}`}
          initial={{ driver_id: editingIntro.driver_id || "", driver_name: editingIntro.driver_name, carrier_name: editingIntro.carrier_name, channel: editingIntro.channel, channel_other: editingIntro.channel_other || "", notes: editingIntro.notes || "" }}
          apps={[]}
          onClose={() => setEditingIntro(null)}
          onSubmit={async (form) => {
            const updated = await api.updateIntroduction(editingIntro.id, form);
            onUpdateIntro(updated);
            toast.success("Introduction updated.");
          }}
          submitLabel="Save Changes"
        />
      )}
      <tr
        data-testid={`app-row-${a.id}`}
        className="border-t border-border hover:bg-muted cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <td className="px-4 py-3 font-medium whitespace-nowrap">
          <div className="flex items-center gap-2">
            {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            {a.first_name} {a.last_name}
          </div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">{a.phone}</td>
        <td className="px-4 py-3 whitespace-nowrap text-sm">{a.position || "—"}</td>
        <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={currentStatus} /></td>
        <td className="px-4 py-3 whitespace-nowrap text-sm">{fmt(a.created_at)}</td>
        <td className="px-4 py-3">
          {a.hubspot_synced ? <CheckCircle2 className="w-5 h-5 text-primary" /> : <XCircle className="w-5 h-5 text-muted-foreground" />}
        </td>
        <td className="px-4 py-3">
          <div className="flex gap-1.5">
            {a.cdl_document_path && (
              <button type="button" data-testid={`view-cdl-${a.id}`}
                onClick={(e) => { e.stopPropagation(); onViewDoc(a.cdl_document_path, "CDL License"); }}
                className="p-1.5 border border-border hover:bg-[#0A0A0A] hover:text-white transition-colors" title="View CDL">
                <FileText className="w-3.5 h-3.5" />
              </button>
            )}
            {a.medical_certificate_path && (
              <button type="button" data-testid={`view-med-${a.id}`}
                onClick={(e) => { e.stopPropagation(); onViewDoc(a.medical_certificate_path, "Medical Certificate"); }}
                className="p-1.5 border border-border hover:bg-[#0A0A0A] hover:text-white transition-colors" title="View Medical Cert">
                <FileText className="w-3.5 h-3.5" />
              </button>
            )}
            {a.signature_path && (
              <button type="button" data-testid={`view-sig-${a.id}`}
                onClick={(e) => { e.stopPropagation(); onViewDoc(a.signature_path, "Signature", a.id); }}
                className="p-1.5 border border-border hover:bg-[#0A0A0A] hover:text-white transition-colors" title="View Signature">
                <Eye className="w-3.5 h-3.5" />
              </button>
            )}
            <button type="button" data-testid={`download-profile-${a.id}`}
              onClick={downloadPDF}
              disabled={downloadingPDF}
              className="p-1.5 border border-border hover:bg-primary hover:text-black hover:border-primary transition-colors disabled:opacity-50" title="Download Driver Profile PDF">
              {downloadingPDF ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        </td>
      </tr>

      {open && (
        <tr data-testid={`app-detail-${a.id}`} className="bg-muted border-t border-border">
          <td colSpan={7} className="px-6 py-5">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <Detail label="Email" value={a.email} />
              <Detail label="CDL Class" value={a.cdl_class} />
              <Detail label="Experience" value={a.cdl_experience} />
              <Detail label="Trailer" value={a.trailer_preference} />
              <Detail label="Endorsements" value={a.endorsements} />
              <Detail label="Emergency Contact" value={a.emergency_contact_name ? `${a.emergency_contact_name} · ${a.emergency_contact_phone}` : null} />
            </div>

            {/* ── SSN secure reveal ── */}
            <div className="mt-4 flex items-center gap-3 flex-wrap">
              <span className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground w-20">SSN</span>
              {ssnVisible && ssnValue !== null ? (
                <span
                  data-testid={`ssn-value-${a.id}`}
                  className="font-mono text-sm bg-amber-50 border border-amber-200 text-amber-900 px-3 py-1 select-all"
                >
                  {ssnValue || "—"}
                </span>
              ) : (
                <span className="font-mono text-sm text-muted-foreground tracking-widest">•••-••-••••</span>
              )}
              <button
                type="button"
                data-testid={`reveal-ssn-${a.id}`}
                onClick={handleRevealSSN}
                disabled={loadingSSN}
                className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.2em] border border-border px-3 py-1.5 hover:bg-[#0A0A0A] hover:text-white hover:border-[#0A0A0A] transition-colors disabled:opacity-50"
              >
                {loadingSSN
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : ssnVisible ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />
                }
                {loadingSSN ? "Loading…" : ssnVisible ? "Hide SSN" : "Show SSN"}
              </button>
            </div>

            {(a.employment_history || []).filter((e) => e.employer).length > 0 && (
              <div className="mt-4">
                <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2">Employment History</p>
                <div className="space-y-1">
                  {a.employment_history.filter((e) => e.employer).map((e, i) => (
                    <p key={i} className="text-sm text-foreground/80">
                      <span className="font-medium">{e.employer}</span>
                      {e.title && ` — ${e.title}`}
                      {(e.from_date || e.to_date) && ` (${e.from_date} – ${e.to_date})`}
                    </p>
                  ))}
                </div>
              </div>
            )}

            {a.message && (
              <div className="mt-4">
                <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Notes</p>
                <p className="text-sm text-foreground/80">{a.message}</p>
              </div>
            )}

            {/* ── Current Fleet / Active Carrier ── */}
            <div className="mt-5 pt-4 border-t border-border" data-testid={`fleet-section-${a.id}`}>
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
                <Truck className="w-3.5 h-3.5 text-primary" /> Current Fleet / Active Carrier
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  data-testid={`fleet-input-${a.id}`}
                  value={fleetValue}
                  onChange={(e) => { e.stopPropagation(); setFleetValue(e.target.value); }}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="e.g. JB Hunt, Hub Group, Werner…"
                  className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A] w-64"
                />
                <button
                  type="button"
                  data-testid={`fleet-save-${a.id}`}
                  disabled={savingFleet}
                  onClick={saveFleet}
                  className="flex items-center gap-1.5 font-head text-xs uppercase tracking-[0.15em] bg-[#0A0A0A] text-white px-4 py-2 hover:bg-primary hover:text-black transition-colors disabled:opacity-50"
                >
                  {savingFleet ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {savingFleet ? "Saving…" : "Update Fleet"}
                </button>
                {!fleetValue && <span className="text-xs text-muted-foreground italic">Not assigned yet</span>}
              </div>
            </div>

            {/* ── Application Sharing Authorization ── */}
            <div className="mt-5 pt-4 border-t border-border" data-testid={`exclusivity-section-${a.id}`}>
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
                <Shield className="w-3.5 h-3.5 text-red-600" /> Application Sharing Authorization
              </p>
              {a.consent_exclusivity ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3 bg-green-50 border border-green-200">
                  <div>
                    <p className="font-head text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Status</p>
                    <p className="text-sm mt-0.5 flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" />
                      <span className="font-medium text-green-700">Signed & Agreed</span>
                    </p>
                  </div>
                  <div>
                    <p className="font-head text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Timestamp</p>
                    <p className="text-sm mt-0.5">{a.exclusivity_timestamp ? fmtFull(a.exclusivity_timestamp) : "—"}</p>
                  </div>
                  <div>
                    <p className="font-head text-[10px] uppercase tracking-[0.2em] text-muted-foreground">IP Address</p>
                    <p className="text-sm mt-0.5 font-mono">{a.applicant_ip || "—"}</p>
                  </div>
                  {a.device_info && (
                    <div className="sm:col-span-2 lg:col-span-3">
                      <p className="font-head text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1">
                        <Monitor className="w-3 h-3" /> Device / Browser
                      </p>
                      <p className="text-xs mt-0.5 text-foreground/60 break-all">{a.device_info}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-muted-foreground p-3 bg-muted/60 border border-border">
                  <XCircle className="w-4 h-4 text-amber-500 shrink-0" />
                  Not signed — applied before this feature was added, or checkbox was not checked.
                </div>
              )}
            </div>

            {/* ── Carrier Introductions for this driver ── */}
            {driverIntros && driverIntros.length > 0 && (
              <div className="mt-5 pt-4 border-t border-border" data-testid={`driver-intros-${a.id}`}>
                <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3 flex items-center gap-2">
                  <BookOpen className="w-3.5 h-3.5 text-primary" /> Carrier Introductions ({driverIntros.length})
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-border">
                    <thead>
                      <tr className="bg-[#0A0A0A] text-white font-head text-[10px] uppercase tracking-[0.1em]">
                        {["ID", "Carrier", "Channel", "Date", "Status", "PDF", "Edit"].map((h) => (
                          <th key={h} className="text-left px-3 py-2">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {driverIntros.map((intro) => {
                        const active = isActiveFn(intro);
                        const left = daysLeftFn(intro);
                        return (
                          <tr key={intro.id} className="border-t border-border hover:bg-white">
                            <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{intro.intro_id}</td>
                            <td className="px-3 py-2 font-medium">{intro.carrier_name}</td>
                            <td className="px-3 py-2">{intro.channel === "Other" ? `Other — ${intro.channel_other}` : intro.channel}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{fmt(intro.created_at)}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {active ? (
                                <span className={`font-head text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 ${left <= 7 ? "bg-amber-50 text-amber-700 border border-amber-300" : "bg-green-50 text-green-700 border border-green-300"}`}>
                                  {left}d left
                                </span>
                              ) : (
                                <span className="font-head text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 bg-slate-100 text-slate-500 border border-slate-200">Expired</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <button type="button" data-testid={`intro-pdf-${intro.id}`}
                                onClick={(e) => { e.stopPropagation(); onDownloadIntroPDF(intro); }}
                                className="p-1.5 border border-border hover:bg-primary hover:text-black transition-colors" title="Download PDF">
                                <FileDown className="w-3.5 h-3.5" />
                              </button>
                            </td>
                            <td className="px-3 py-2">
                              <button type="button" data-testid={`intro-edit-driver-${intro.id}`}
                                onClick={(e) => { e.stopPropagation(); setEditingIntro(intro); }}
                                className="p-1.5 border border-border hover:bg-[#0A0A0A] hover:text-white transition-colors" title="Edit Introduction">
                                <Edit2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── Status update ── */}
            <div className="mt-6 pt-5 border-t border-border flex items-center gap-3 flex-wrap">
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Update Stage:</p>
              <select
                data-testid={`status-select-${a.id}`}
                value={newStatus}
                onChange={(e) => { e.stopPropagation(); setNewStatus(e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]"
              >
                {STATUS_OPTIONS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <button type="button" data-testid={`status-save-${a.id}`}
                disabled={saving || newStatus === currentStatus}
                onClick={saveStatus}
                className="flex items-center gap-2 font-head text-xs uppercase tracking-[0.15em] bg-primary text-black px-4 py-2 hover:bg-[#0A0A0A] hover:text-primary transition-colors disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
              {newStatus === currentStatus && <span className="text-xs text-muted-foreground">No changes</span>}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className="text-sm mt-0.5">{value || "—"}</p>
    </div>
  );
}

/* ─── Introductions Log Panel ─── */
function IntroductionsPanel({ introductions, loading, apps, onCreated, onDelete, onDownloadPDF, onUpdate }) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIntro, setEditingIntro] = useState(null);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [activeOnly, setActiveOnly] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const filtered = introductions.filter((i) => {
    const q = search.toLowerCase();
    if (q && !i.driver_name.toLowerCase().includes(q) && !i.carrier_name.toLowerCase().includes(q)) return false;
    if (fromDate && new Date(i.created_at) < new Date(fromDate)) return false;
    if (toDate && new Date(i.created_at) > new Date(toDate + "T23:59:59")) return false;
    if (activeOnly && !isActiveFn(i)) return false;
    return true;
  });

  const activeCount = introductions.filter(isActiveFn).length;

  return (
    <div data-testid="introductions-panel">
      {showCreateModal && (
        <IntroFormModal
          title="Log New Carrier Introduction"
          subtitle={`Admin: ${ADMIN_NAME}`}
          initial={NEW_INTRO_DEFAULT}
          apps={apps}
          onClose={() => setShowCreateModal(false)}
          onSubmit={async (form) => {
            const intro = await api.createIntroduction(form);
            toast.success(`Introduction logged: ${intro.intro_id}`);
            onCreated(intro);
          }}
          submitLabel="Log Introduction"
        />
      )}

      {editingIntro && (
        <IntroFormModal
          title="Edit Introduction"
          subtitle={`ID: ${editingIntro.intro_id}`}
          initial={{ driver_id: editingIntro.driver_id || "", driver_name: editingIntro.driver_name, carrier_name: editingIntro.carrier_name, channel: editingIntro.channel, channel_other: editingIntro.channel_other || "", notes: editingIntro.notes || "" }}
          apps={apps}
          onClose={() => setEditingIntro(null)}
          onSubmit={async (form) => {
            const updated = await api.updateIntroduction(editingIntro.id, form);
            onUpdate(updated);
            toast.success("Introduction updated.");
          }}
          submitLabel="Save Changes"
        />
      )}

      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white border border-border p-8 max-w-sm w-full text-center">
            <Trash2 className="w-10 h-10 text-red-600 mx-auto mb-4" />
            <p className="font-head text-sm uppercase tracking-[0.15em] mb-3">Delete Introduction?</p>
            <p className="text-sm text-muted-foreground mb-6">This action cannot be undone. The record will be permanently removed.</p>
            <div className="flex gap-3 justify-center">
              <button data-testid="confirm-delete-intro"
                onClick={() => { onDelete(confirmDeleteId); setConfirmDeleteId(null); }}
                className="font-head text-xs uppercase tracking-[0.15em] bg-red-600 text-white px-5 py-2.5 hover:bg-red-700 transition-colors">
                Delete
              </button>
              <button onClick={() => setConfirmDeleteId(null)}
                className="font-head text-xs uppercase tracking-[0.15em] border-2 border-border px-5 py-2.5 hover:border-[#0A0A0A] transition-colors">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="font-head text-xs uppercase tracking-[0.25em] text-muted-foreground">Admin: {ADMIN_NAME}</p>
          <h2 className="font-head text-2xl uppercase tracking-[0.15em] mt-1">Carrier Introduction Log</h2>
        </div>
        <button data-testid="new-intro-btn" onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.15em] bg-[#0A0A0A] text-white px-5 py-3 hover:bg-primary hover:text-black transition-colors">
          <PlusCircle className="w-4 h-4" /> Log New Introduction
        </button>
      </div>

      {/* Stats row */}
      {introductions.length > 0 && (() => {
        const totalActive   = introductions.filter(isActiveFn).length;
        const expiring      = introductions.filter((i) => { const d = daysLeftFn(i); return d > 0 && d <= 7; }).length;
        const expired       = introductions.filter((i) => !isActiveFn(i)).length;
        return (
          <div className="grid grid-cols-4 gap-px bg-border border border-border mb-6" data-testid="intro-stats-row">
            <div className="bg-white px-5 py-4">
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Total</p>
              <p className="font-head text-3xl font-bold mt-1">{introductions.length}</p>
            </div>
            <div className="bg-white px-5 py-4">
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Active</p>
              <p className="font-head text-3xl font-bold mt-1 text-green-700">{totalActive}</p>
            </div>
            <div className="bg-white px-5 py-4">
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3 text-amber-500" /> Expiring ≤7d
              </p>
              <p className={`font-head text-3xl font-bold mt-1 ${expiring > 0 ? "text-amber-600" : "text-muted-foreground"}`}>{expiring}</p>
            </div>
            <div className="bg-white px-5 py-4">
              <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Expired</p>
              <p className="font-head text-3xl font-bold mt-1 text-slate-400">{expired}</p>
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-4 mb-4 p-4 border border-border bg-muted/50">
        <Filter className="w-4 h-4 text-muted-foreground shrink-0 mt-auto mb-2" />
        <div>
          <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Search</label>
          <input data-testid="intro-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Driver or carrier name…"
            className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A] w-48" />
        </div>
        <div>
          <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">From Date</label>
          <input type="date" data-testid="intro-filter-from" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
            className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" />
        </div>
        <div>
          <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">To Date</label>
          <input type="date" data-testid="intro-filter-to" value={toDate} onChange={(e) => setToDate(e.target.value)}
            className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" />
        </div>
        {/* Active Only toggle */}
        <div className="mt-auto mb-0">
          <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Exclusivity</label>
          <button
            type="button"
            data-testid="intro-active-only-toggle"
            onClick={() => setActiveOnly((v) => !v)}
            className={`flex items-center gap-2 font-head text-xs uppercase tracking-[0.15em] px-3 py-2 border-2 transition-colors ${
              activeOnly
                ? "border-green-600 bg-green-600 text-white"
                : "border-border text-muted-foreground hover:border-green-600 hover:text-green-700"
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            Active Only ({activeCount})
          </button>
        </div>

        {(search || fromDate || toDate || activeOnly) && (
          <button type="button" data-testid="intro-clear-filters"
            onClick={() => { setSearch(""); setFromDate(""); setToDate(""); setActiveOnly(false); }}
            className="flex items-center gap-1 font-head text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-[#0A0A0A] mt-auto mb-2">
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
        <p className="font-head text-xs uppercase tracking-[0.15em] text-muted-foreground mt-auto mb-2 ml-auto">
          {filtered.length} record{filtered.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#0A0A0A] text-white font-head uppercase tracking-[0.1em] text-xs">
                {["ID", "Driver", "Carrier", "Channel", "Date", "Excl. Status", "Admin", "PDF", "Edit", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">
                    {introductions.length === 0 ? "No introductions logged yet." : "No results match your filters."}
                  </td>
                </tr>
              )}
              {filtered.map((intro) => {
                const active = isActiveFn(intro);
                const left = daysLeftFn(intro);
                return (
                  <tr key={intro.id} data-testid={`intro-row-${intro.id}`} className="border-t border-border hover:bg-muted">
                    <td className="px-4 py-3 font-mono text-xs text-muted-foreground whitespace-nowrap">{intro.intro_id}</td>
                    <td className="px-4 py-3 font-medium whitespace-nowrap">{intro.driver_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{intro.carrier_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {intro.channel === "Other" ? `Other — ${intro.channel_other}` : intro.channel}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">{fmt(intro.created_at)}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {active ? (
                        <span className={`font-head text-[10px] uppercase tracking-[0.1em] px-2 py-1 border ${left <= 7 ? "bg-amber-50 text-amber-700 border-amber-300" : "bg-green-50 text-green-700 border-green-300"}`}>
                          {left}d left
                        </span>
                      ) : (
                        <span className="font-head text-[10px] uppercase tracking-[0.1em] px-2 py-1 bg-slate-100 text-slate-400 border border-slate-200">Expired</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">{intro.admin_name}</td>
                    <td className="px-4 py-3">
                      <button type="button" data-testid={`intro-pdf-btn-${intro.id}`}
                        onClick={() => onDownloadPDF(intro)}
                        className="inline-flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.1em] border border-border px-2.5 py-1.5 hover:bg-primary hover:text-black hover:border-primary transition-colors"
                        title="Download PDF">
                        <FileDown className="w-3.5 h-3.5" /> PDF
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" data-testid={`intro-edit-btn-${intro.id}`}
                        onClick={() => setEditingIntro(intro)}
                        className="p-1.5 border border-border hover:bg-[#0A0A0A] hover:text-white transition-colors text-muted-foreground"
                        title="Edit">
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" data-testid={`intro-delete-btn-${intro.id}`}
                        onClick={() => setConfirmDeleteId(intro.id)}
                        className="p-1.5 border border-border hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors text-muted-foreground"
                        title="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Main Admin component ─── */
export default function Admin() {
  const [authed, setAuthed] = useState(!!getToken());
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState("applications");
  const [introductions, setIntroductions] = useState([]);
  const [introLoading, setIntroLoading] = useState(false);
  const [chatLeads, setChatLeads] = useState([]);
  const [chatLeadsLoading, setChatLeadsLoading] = useState(false);
  const [filterPos, setFilterPos] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [search, setSearch] = useState("");
  const [docModal, setDocModal] = useState(null);
  const [hubCreds, setHubCreds] = useState([]);
  const [showHubCreds, setShowHubCreds] = useState(false);
  const [showPassFor, setShowPassFor] = useState({});

  const handleStatusChange = useCallback((id, newStatus) => {
    setApps((prev) => prev.map((a) => a.id === id ? { ...a, status: newStatus } : a));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([api.listApplications(), api.applicationStats()]);
      setApps(a); setStats(s);
    } catch (e) {
      if (e?.response?.status === 401) { clearToken(); setAuthed(false); toast.error("Session expired."); }
      else toast.error("Failed to load applications.");
    } finally { setLoading(false); }
  }, []);

  const loadIntroductions = useCallback(async () => {
    setIntroLoading(true);
    try { setIntroductions(await api.listIntroductions()); }
    catch { toast.error("Failed to load introductions."); }
    finally { setIntroLoading(false); }
  }, []);

  const loadChatLeads = useCallback(async () => {
    setChatLeadsLoading(true);
    try { setChatLeads(await api.listChatLeads()); }
    catch { toast.error("Failed to load chat leads."); }
    finally { setChatLeadsLoading(false); }
  }, []);

  useEffect(() => {
    if (authed) {
      load(); loadIntroductions(); loadChatLeads();
      api.adminGetHubCredentials().then((r) => setHubCreds(r.hubs || [])).catch(() => {});
    }
  }, [authed, load, loadIntroductions, loadChatLeads]);

  const logout = () => { clearToken(); setAuthed(false); setApps([]); setStats(null); setIntroductions([]); setChatLeads([]); };

  const exportCSV = async () => {
    setExporting(true);
    try { const blob = await api.exportApplications(); triggerDownload(blob, "hylas-applications.csv"); toast.success("Export downloaded."); }
    catch { toast.error("Export failed."); }
    finally { setExporting(false); }
  };

  const handleIntroCreated = (intro) => setIntroductions((prev) => [intro, ...prev]);

  const handleIntroDelete = async (id) => {
    try { await api.deleteIntroduction(id); setIntroductions((prev) => prev.filter((i) => i.id !== id)); toast.success("Introduction deleted."); }
    catch { toast.error("Delete failed."); }
  };

  const handleIntroUpdate = (updated) => {
    setIntroductions((prev) => prev.map((i) => i.id === updated.id ? updated : i));
  };

  const handleDownloadIntroPDF = async (intro) => {
    try { const blob = await api.getIntroductionPDF(intro.id); triggerDownload(blob, `hylas-intro-${intro.intro_id}.pdf`); toast.success("PDF downloaded."); }
    catch { toast.error("Could not download PDF."); }
  };

  const handleDownloadDriverPDF = async (appId) => {
    return await api.getDriverProfilePDF(appId);
  };

  const allPositions = [...new Set(apps.map((a) => a.position).filter(Boolean))].sort();
  const expiringSoon = introductions.filter((i) => { const d = daysLeftFn(i); return d > 0 && d <= 7; });
  const filtered = apps.filter((a) => {
    if (filterPos && a.position !== filterPos) return false;
    if (filterFrom && new Date(a.created_at) < new Date(filterFrom)) return false;
    if (filterTo && new Date(a.created_at) > new Date(filterTo + "T23:59:59")) return false;
    if (search) {
      const q = search.toLowerCase();
      const name = `${a.first_name} ${a.last_name}`.toLowerCase();
      if (!name.includes(q) && !(a.phone || "").includes(q) && !(a.email || "").toLowerCase().includes(q)) return false;
    }
    return true;
  });

  if (!authed) return (
    <>
      <SEOHead title="Admin Dashboard" description="Hylas Companion Admin" path="/admin" noindex={true} />
      <AdminLogin onSuccess={() => setAuthed(true)} />
    </>
  );

  return (
    <div data-testid="admin-page" className="pt-32 md:pt-40 pb-24 max-w-7xl mx-auto px-6 md:px-12">
      <SEOHead title="Admin Dashboard" description="Hylas Companion Admin" path="/admin" noindex={true} />
      {docModal && <DocModal path={docModal.path} label={docModal.label} appId={docModal.appId}
          onSignatureCleared={(id) => setApps(prev => prev.map(a => a.id === id ? { ...a, signature_path: "" } : a))}
          onClose={() => setDocModal(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <Overline>Recruiting Dashboard</Overline>
          <h1 className="font-head text-3xl md:text-5xl font-bold uppercase tracking-[0.15em] mt-4">
            {activeTab === "applications" ? "Driver Applications" : "Introduction Log"}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {activeTab === "applications" && (
            <button onClick={exportCSV} disabled={exporting} data-testid="admin-export"
              className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.15em] bg-primary text-black px-5 py-3 hover:bg-[#0A0A0A] hover:text-primary transition-colors disabled:opacity-60">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Export CSV
            </button>
          )}
          <button onClick={() => { load(); loadIntroductions(); }} data-testid="admin-refresh"
            className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.15em] border-2 border-[#0A0A0A] px-5 py-3 hover:bg-[#0A0A0A] hover:text-white transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading || introLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button onClick={logout} data-testid="admin-logout"
            className="inline-flex items-center gap-2 font-head text-sm uppercase tracking-[0.15em] text-muted-foreground hover:text-[#0A0A0A] px-3 py-3 transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      {/* Hub Credentials Panel */}
      <div className="mb-8 border border-gray-200 bg-gray-50" data-testid="admin-hub-creds">
        <button
          data-testid="admin-hub-creds-toggle"
          onClick={() => setShowHubCreds((v) => !v)}
          className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-gray-100 transition-colors"
        >
          <Lock className="w-4 h-4 shrink-0 text-[#0A0A0A]" />
          <div className="flex-1">
            <p className="font-head text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]">Platform Hub Credentials</p>
            <p className="text-xs text-muted-foreground mt-0.5">All Market & main site login credentials — visible to admin only</p>
          </div>
          <span className="font-head text-[9px] px-2 py-1 uppercase tracking-[0.1em] border-2 border-[#0A0A0A] text-[#0A0A0A]">
            {showHubCreds ? "HIDE" : "SHOW"}
          </span>
        </button>
        {showHubCreds && (
          <div className="border-t border-gray-200 divide-y divide-gray-200">
            {hubCreds.length === 0 ? (
              <p className="px-5 py-4 text-sm text-muted-foreground">Credentials not loaded — check market config is set up.</p>
            ) : hubCreds.map((h) => (
              <div key={h.hub} className="px-5 py-3 flex items-center gap-4 flex-wrap">
                <div className="w-24 shrink-0">
                  <span className="font-head text-[9px] uppercase tracking-[0.1em] border border-[#0A0A0A] px-2 py-0.5 text-[#0A0A0A]">
                    {h.hub}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-head text-xs font-bold text-[#0A0A0A] mb-0.5">{h.label}</p>
                  <p className="text-xs text-muted-foreground font-mono">User: <span className="text-[#0A0A0A]">{h.username}</span></p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-muted-foreground font-mono">
                      Pass: <span className="text-[#0A0A0A]">{showPassFor[h.hub] ? h.password : "••••••••••"}</span>
                    </p>
                    <button
                      onClick={() => setShowPassFor((prev) => ({ ...prev, [h.hub]: !prev[h.hub] }))}
                      className="text-[10px] font-head uppercase text-muted-foreground hover:text-[#0A0A0A] transition-colors"
                      data-testid={`admin-show-pass-${h.hub}`}
                    >
                      {showPassFor[h.hub] ? "hide" : "reveal"}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="flex border-b-2 border-border mb-8" data-testid="admin-tabs">
        {[
          { key: "applications", icon: <FileText className="w-4 h-4" />, label: "Applications", count: apps.length },
          { key: "introductions", icon: <BookOpen className="w-4 h-4" />, label: "Introduction Log", count: introductions.length },
          { key: "chat_leads", icon: <MessageSquare className="w-4 h-4" />, label: "Mila Chat Leads", count: chatLeads.length },
        ].map((tab) => (
          <button key={tab.key} data-testid={`tab-${tab.key}`} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 font-head text-xs uppercase tracking-[0.2em] px-5 py-3 border-b-2 -mb-[2px] transition-colors ${
              activeTab === tab.key ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-muted-foreground hover:text-[#0A0A0A]"
            }`}>
            {tab.icon} {tab.label}
            <span className="ml-1 font-sans font-normal text-xs text-muted-foreground">({tab.count})</span>
            {tab.key === "introductions" && expiringSoon.length > 0 && (
              <span
                data-testid="expiring-tab-badge"
                className="ml-1 bg-amber-500 text-white font-head text-[9px] px-1.5 py-0.5 min-w-[18px] text-center leading-none"
              >
                {expiringSoon.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Expiry Alert Banner */}
      {expiringSoon.length > 0 && (
        <div
          data-testid="expiry-alert-banner"
          className="mb-6 border-2 border-amber-400 bg-amber-50 px-5 py-4"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="font-head text-sm uppercase tracking-[0.15em] text-amber-800 mb-2">
                  {expiringSoon.length} Exclusivity Window{expiringSoon.length > 1 ? "s" : ""} Expiring Within 7 Days
                </p>
                <div className="space-y-1">
                  {expiringSoon.map((i) => (
                    <p key={i.id} className="text-sm text-amber-700 flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{i.driver_name}</span>
                      <span className="text-amber-400">→</span>
                      <span>{i.carrier_name}</span>
                      <span
                        className={`font-head text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border ${
                          daysLeftFn(i) <= 2
                            ? "bg-red-50 text-red-700 border-red-300"
                            : "bg-amber-100 text-amber-800 border-amber-300"
                        }`}
                      >
                        {daysLeftFn(i)}d left
                      </span>
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <button
              data-testid="expiry-review-btn"
              onClick={() => setActiveTab("introductions")}
              className="font-head text-xs uppercase tracking-[0.2em] border-2 border-amber-600 text-amber-700 px-4 py-2.5 hover:bg-amber-600 hover:text-white transition-colors whitespace-nowrap shrink-0 self-start"
            >
              Review Now
            </button>
          </div>
        </div>
      )}

      {/* ── Applications Tab ── */}
      {activeTab === "applications" && (
        <>
          {stats && (
            <div className="grid grid-cols-3 gap-px bg-border border border-border mb-8 max-w-lg">
              {[
                { label: "Total", val: stats.total, color: "" },
                { label: "HubSpot", val: stats.synced_to_hubspot, color: " text-primary" },
                { label: "With Docs", val: stats.with_documents ?? "—", color: " text-primary" },
              ].map((s) => (
                <div key={s.label} className="bg-white p-5">
                  <p className="font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{s.label}</p>
                  <p className={`font-head text-4xl font-bold mt-1${s.color}`}>{s.val}</p>
                </div>
              ))}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap items-end gap-4 mb-4 p-4 border border-border bg-muted/50">
            <Filter className="w-4 h-4 text-muted-foreground shrink-0 mt-auto mb-2" />
            <div>
              <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Search</label>
              <input data-testid="admin-search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, phone, email…"
                className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A] w-52" />
            </div>
            <div>
              <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">Position</label>
              <select data-testid="admin-filter-pos" value={filterPos} onChange={(e) => setFilterPos(e.target.value)}
                className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]">
                <option value="">All Positions</option>
                {allPositions.map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">From Date</label>
              <input type="date" data-testid="admin-filter-from" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)}
                className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" />
            </div>
            <div>
              <label className="block font-head text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">To Date</label>
              <input type="date" data-testid="admin-filter-to" value={filterTo} onChange={(e) => setFilterTo(e.target.value)}
                className="border border-border bg-white px-3 py-2 text-sm focus:outline-none focus:border-[#0A0A0A]" />
            </div>
            {(search || filterPos || filterFrom || filterTo) && (
              <button type="button" data-testid="admin-clear-filters"
                onClick={() => { setSearch(""); setFilterPos(""); setFilterFrom(""); setFilterTo(""); }}
                className="flex items-center gap-1 font-head text-xs uppercase tracking-[0.15em] text-muted-foreground hover:text-[#0A0A0A] mt-auto mb-2">
                <X className="w-3.5 h-3.5" /> Clear
              </button>
            )}
            <p className="font-head text-xs uppercase tracking-[0.15em] text-muted-foreground mt-auto mb-2 ml-auto">
              {filtered.length} result{filtered.length !== 1 ? "s" : ""}
            </p>
          </div>

          {/* Applications table */}
          <div className="overflow-x-auto border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#0A0A0A] text-white font-head uppercase tracking-[0.1em] text-xs">
                  {["Driver", "Phone", "Position", "Stage", "Applied", "HubSpot", "Docs"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-muted-foreground">
                      {apps.length === 0 ? "No applications yet." : "No results match your filters."}
                    </td>
                  </tr>
                )}
                {filtered.map((a) => (
                  <AppRow
                    key={a.id}
                    a={a}
                    driverIntros={introductions.filter((i) => i.driver_id === a.id)}
                    onViewDoc={(path, label, appId) => setDocModal({ path, label, appId })}
                    onStatusChange={handleStatusChange}
                    onUpdateIntro={handleIntroUpdate}
                    onDownloadIntroPDF={handleDownloadIntroPDF}
                    onDownloadDriverPDF={handleDownloadDriverPDF}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Introductions Tab ── */}
      {activeTab === "introductions" && (
        <IntroductionsPanel
          introductions={introductions}
          loading={introLoading}
          apps={apps}
          onCreated={handleIntroCreated}
          onDelete={handleIntroDelete}
          onDownloadPDF={handleDownloadIntroPDF}
          onUpdate={handleIntroUpdate}
        />
      )}

      {/* ── Mila Chat Leads Tab ── */}
      {activeTab === "chat_leads" && (
        <div data-testid="chat-leads-panel">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <p className="font-head text-[10px] uppercase tracking-[0.35em] text-primary mb-1">Mila Recruiter Bot</p>
              <h2 className="font-head text-2xl uppercase tracking-[0.12em]">Chat Leads</h2>
            </div>
            <button onClick={loadChatLeads} disabled={chatLeadsLoading}
              className="inline-flex items-center gap-2 font-head text-xs uppercase tracking-[0.2em] border-2 border-[#0A0A0A] px-4 py-2.5 hover:bg-[#0A0A0A] hover:text-white transition-colors disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${chatLeadsLoading ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>

          {chatLeadsLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : chatLeads.length === 0 ? (
            <div className="border border-dashed border-border py-16 text-center">
              <MessageSquare className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="font-head text-xs uppercase tracking-[0.25em] text-muted-foreground">No chat leads yet</p>
              <p className="text-sm text-muted-foreground mt-1">Leads collected by Mila will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-border">
              <table className="w-full text-sm">
                <thead className="bg-[#0A0A0A] text-white">
                  <tr>
                    {["Name", "Phone", "Position Interest", "Truck Pref.", "CDL-A", "Date", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left font-head text-[9px] uppercase tracking-[0.25em] whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {chatLeads.map((lead) => (
                    <tr key={lead.id} data-testid={`chat-lead-${lead.id}`} className="border-b border-border hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-medium">{lead.name}</td>
                      <td className="px-4 py-3">
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 text-primary hover:underline whitespace-nowrap">
                          <Phone className="w-3 h-3" />{lead.phone}
                        </a>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-head text-[10px] uppercase tracking-[0.15em] border border-primary/40 text-primary px-2 py-0.5">
                          {lead.position_interest}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-foreground/60">{lead.truck_preference || "—"}</td>
                      <td className="px-4 py-3 text-foreground/60">{lead.has_cdl || "—"}</td>
                      <td className="px-4 py-3 text-foreground/50 text-xs whitespace-nowrap">
                        {new Date(lead.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={async () => {
                            try { await api.deleteChatLead(lead.id); setChatLeads((prev) => prev.filter((l) => l.id !== lead.id)); toast.success("Lead deleted."); }
                            catch { toast.error("Delete failed."); }
                          }}
                          className="text-muted-foreground hover:text-red-600 transition-colors p-1"
                          title="Delete lead"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Login page ─── */
function AdminLogin({ onSuccess }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [cooldownEnd, setCooldownEnd] = useState(null);
  const [cooldownDisplay, setCooldownDisplay] = useState("");

  useEffect(() => {
    if (!cooldownEnd) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
      if (remaining <= 0) { setCooldownEnd(null); setCooldownDisplay(""); return; }
      const h = Math.floor(remaining / 3600);
      const m = Math.floor((remaining % 3600) / 60);
      const s = remaining % 60;
      setCooldownDisplay(h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [cooldownEnd]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setErrorMsg("");
    setSubmitting(true);
    try {
      const { token } = await api.adminLogin(username, password);
      setToken(token); toast.success("Welcome back."); onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 429 && typeof detail === "object" && detail?.cooldown_seconds) {
        setCooldownEnd(Date.now() + detail.cooldown_seconds * 1000);
        setErrorMsg(detail.message || "Too many attempts. Please wait.");
      } else {
        const msg = typeof detail === "string" ? detail : "Incorrect credentials.";
        setErrorMsg(msg);
        toast.error(msg.slice(0, 80));
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen flex items-center justify-center px-6 pt-20">
      <form onSubmit={submit} className="w-full max-w-sm bg-white border border-border p-8 md:p-10">
        <span className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center mb-6">
          <Lock className="w-5 h-5 text-primary" />
        </span>
        <Overline>Staff Only</Overline>
        <h1 className="font-head text-2xl md:text-3xl uppercase tracking-[0.12em] mt-3 mb-8">Dashboard Login</h1>
        <label className="block font-head text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Username / Email</label>
        <input type="text" data-testid="admin-username-input" value={username} onChange={(e) => setUsername(e.target.value)}
          className="w-full bg-transparent border-b-2 border-border py-3 text-[#0A0A0A] focus:outline-none focus:border-[#0A0A0A] transition-colors mb-6"
          placeholder="admin@hylascompanion.us" autoFocus autoComplete="username" />
        <label className="block font-head text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Password</label>
        <input type="password" data-testid="admin-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
          className="w-full bg-transparent border-b-2 border-border py-3 text-[#0A0A0A] focus:outline-none focus:border-[#0A0A0A] transition-colors"
          placeholder="••••••••" autoComplete="current-password" />
        {errorMsg && (
          <p className="mt-3 text-sm text-red-600 leading-snug">{errorMsg}</p>
        )}
        {cooldownDisplay && (
          <p className="mt-1 text-xs text-orange-600 font-mono">Locked for: {cooldownDisplay}</p>
        )}
        <button type="submit" data-testid="admin-login-btn" disabled={submitting || !!cooldownEnd}
          className="mt-8 w-full font-head text-base uppercase tracking-[0.2em] bg-primary text-black py-4 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2">
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Verifying</> : "Enter"}
        </button>
      </form>
    </div>
  );
}
